import type {
    SketchDimensionArcCondition,
    SketchDimensionReferenceRecord,
    SketchDimensionTopologyRole,
} from '../SketchDimensionTypes';

/** 点类拓扑角色集合：这些角色必须携带 pointId。 */
const POINT_TOPOLOGY_ROLES: ReadonlySet<SketchDimensionTopologyRole> = new Set([
    'point',
    'endpoint-start',
    'endpoint-end',
    'center',
    'midpoint',
    'quadrant',
]);

/**
 * 尺寸稳定引用对象。
 *
 * 设计说明：
 * 1. 构造即校验：非法记录（空 ID、点角色缺 pointId、非法附着条件）直接抛错，
 *    保证进入聚合的引用永远合法；
 * 2. `signature` 是引用的稳定语义签名，用于悬空修复的原引用记录与推断器缓存 key；
 *    签名只由持久身份字段组成，不含运行时信息。
 */
export class SketchDimensionReference {
    /** 引用纯数据记录。 */
    private readonly record: SketchDimensionReferenceRecord;

    /**
     * 创建尺寸引用值对象。
     * @param record 引用纯数据记录。
     * @throws 当实体持久 ID 为空，或选择顺序不是非负整数时抛出错误。
     * @throws 当点拓扑角色未携带 `pointId` 时抛出错误。
     */
    public constructor(record: SketchDimensionReferenceRecord) {
        if (!record.entityId) {
            throw new Error('尺寸引用缺少实体持久 ID。');
        }
        if (!Number.isInteger(record.selectionOrder) || record.selectionOrder < 0) {
            throw new Error(`尺寸引用选择顺序非法：${String(record.selectionOrder)}`);
        }
        if (POINT_TOPOLOGY_ROLES.has(record.topologyRole) && !record.pointId) {
            throw new Error(`尺寸引用拓扑角色 ${record.topologyRole} 必须携带 pointId。`);
        }
        this.record = {...record};
    }

    /** 目标实体持久 ID。 */
    public get entityId(): string {
        return this.record.entityId;
    }

    /** 拓扑角色。 */
    public get topologyRole(): SketchDimensionTopologyRole {
        return this.record.topologyRole;
    }

    /** 点持久 ID；非点角色为 undefined。 */
    public get pointId(): string | undefined {
        return this.record.pointId;
    }

    /** 选择顺序。 */
    public get selectionOrder(): number {
        return this.record.selectionOrder;
    }

    /** 圆系附着条件；非圆系引用为 undefined。 */
    public get arcCondition(): SketchDimensionArcCondition | undefined {
        return this.record.arcCondition;
    }

    /** 是否外部投影引用。 */
    public isExternal(): boolean {
        return Boolean(this.record.externalSourcePath);
    }

    /**
     * 引用的稳定语义签名。
     *
     * @returns 由持久身份字段拼接的签名字符串。
     */
    public get signature(): string {
        const parts = [
            this.record.entityId,
            this.record.topologyRole,
            this.record.pointId ?? '',
            this.record.subEntityPath ?? '',
            this.record.externalSourcePath ?? '',
        ];

        return parts.join('|');
    }

    /**
     * 输出纯数据记录副本。
     *
     * @returns 引用记录。
     */
    public toRecord(): SketchDimensionReferenceRecord {
        return {...this.record};
    }

    /**
     * 派生附着条件变更后的新引用。
     *
     * @param arcCondition 新附着条件。
     * @returns 新引用对象；本对象保持不变。
     */
    public withArcCondition(arcCondition: SketchDimensionArcCondition): SketchDimensionReference {
        return new SketchDimensionReference({...this.record, arcCondition});
    }

    /**
     * 判断与另一引用是否语义相同（含选择顺序与附着条件）。
     *
     * @param other 另一引用。
     * @returns 是否语义相同。
     */
    public equals(other: SketchDimensionReference): boolean {
        return this.signature === other.signature
            && this.record.selectionOrder === other.record.selectionOrder
            && this.record.arcCondition === other.record.arcCondition;
    }

    /**
     * 计算有序引用列表的组合签名。
     *
     * @param references 引用列表。
     * @returns 组合签名。
     */
    public static combinedSignature(references: readonly SketchDimensionReference[]): string {
        return references
            .map((reference) => reference.signature)
            .join('&&');
    }
}
